/**
 * client/tutorial-arena.ts
 * Responsabilidad: Tutorial Dojo 2.0 interactivo ejecutado 100% sobre Canvas a 60 FPS.
 * 
 * Fases del Dojo 2.0 (6 Fases Interactivas):
 * 1. Movimiento y Crecimiento (4 orbes de energía elemental).
 * 2. Cambio de Elemento ([1-5]) y Robo de Masa por Contacto en Franja Exterior (Gólem de Fuego 🔥 vs Agua 💧).
 * 3. Desactivación de Zona de Peligro Mortal y Devoración del Gólem debilitado.
 * 4. Pociones Tácticas [Q/W/E] (0.5s de canalización y Elixir de Velocidad).
 * 5. Reliquias y Power-Ups: Letrero de 5 Power-Ups + Prueba de Escudo 🛡️ contra Bot Titán Voltaico.
 * 6. Simulación en vivo del Evento "🍬 Lluvia de Comida" + Letrero de 4 Eventos de la Arena y Graduación.
 */

import { safeGetStorage, safeSetStorage } from "./utils";
import { questManager } from "./quests";
import { AudioManager } from "./audio";

interface TutorialOptions {
    isGuest: boolean;
    alreadyClaimed: boolean;
}

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    color: string;
    alpha: number;
    life: number;
    maxLife: number;
}

interface FloatingText {
    x: number;
    y: number;
    text: string;
    color: string;
    alpha: number;
    life: number;
    maxLife: number;
}

interface Shockwave {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    alpha: number;
}

interface EnergyOrb {
    x: number;
    y: number;
    baseX: number;
    baseY: number;
    radius: number;
    color: string;
    glowColor: string;
    pulseOffset: number;
    collected: boolean;
}

interface EventFood {
    x: number;
    y: number;
    radius: number;
    collected: boolean;
    pulseOffset: number;
}

type ElementType = "fire" | "water" | "plant" | "electric" | "earth";

const ELEMENT_DATA: Record<ElementType, { name: string; icon: string; glow: string; fill: string; stroke: string }> = {
    fire: { name: "Fuego", icon: "🔥", glow: "rgba(239, 68, 68, 0.45)", fill: "#b91c1c", stroke: "#f87171" },
    water: { name: "Agua", icon: "💧", glow: "rgba(56, 189, 248, 0.45)", fill: "#0284c7", stroke: "#38bdf8" },
    plant: { name: "Planta", icon: "🌿", glow: "rgba(34, 197, 94, 0.45)", fill: "#15803d", stroke: "#4ade80" },
    electric: { name: "Eléctrico", icon: "⚡", glow: "rgba(234, 179, 8, 0.45)", fill: "#ca8a04", stroke: "#fde047" },
    earth: { name: "Tierra", icon: "🌍", glow: "rgba(180, 83, 9, 0.45)", fill: "#78350f", stroke: "#f59e0b" },
};

export function startTutorialArena(options: TutorialOptions): void {
    const overlay = document.getElementById("tutorial-overlay");
    const canvas = document.getElementById("tutorial-canvas") as HTMLCanvasElement | null;
    const exitBtn = document.getElementById("tutorial-exit-btn");
    const phaseTag = document.getElementById("tutorial-phase-tag");
    const phaseTitle = document.getElementById("tutorial-phase-title");
    const phaseDesc = document.getElementById("tutorial-phase-desc");
    const progressBar = document.getElementById("tutorial-progress-bar");
    const wheelModal = document.getElementById("tutorial-wheel-modal");
    const finishModal = document.getElementById("tutorial-finish-modal");
    const finishTitle = document.getElementById("tutorial-finish-title");
    const finishDesc = document.getElementById("tutorial-finish-desc");
    const finishBadge = document.getElementById("tutorial-reward-badge");
    const toArenaBtn = document.getElementById("tutorial-to-arena-btn");
    const aliasModal = document.getElementById("alias-modal");

    // Sprint 32: Controles interactivos y modales del dojo
    const tutorialControlsWrap = document.getElementById("tutorial-controls-wrap");
    const tutorialPotionBtn = document.getElementById("tutorial-potion-btn");
    const tutorialElementBar = document.getElementById("tutorial-element-bar");
    const tutorialPowerupsModal = document.getElementById("tutorial-powerups-modal");
    const tutorialPowerupsContinueBtn = document.getElementById("tutorial-powerups-continue-btn");
    const tutorialEventsModal = document.getElementById("tutorial-events-modal");
    const tutorialEventsContinueBtn = document.getElementById("tutorial-events-continue-btn");
    const elBtns = document.querySelectorAll<HTMLButtonElement>(".tutorial-el-btn");

    if (!overlay || !canvas) {
        console.error("[TutorialArena] Elementos del DOM no encontrados.");
        return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Ocultar modal de alias y mostrar overlay
    if (aliasModal) aliasModal.classList.add("hidden");
    overlay.classList.remove("hidden");
    if (wheelModal) wheelModal.classList.add("hidden");
    if (finishModal) finishModal.classList.add("hidden");
    if (tutorialControlsWrap) tutorialControlsWrap.classList.add("hidden");
    if (tutorialPowerupsModal) tutorialPowerupsModal.classList.add("hidden");
    if (tutorialEventsModal) tutorialEventsModal.classList.add("hidden");

    let animationFrameId: number | null = null;
    let isActive = true;
    let isPaused = false;
    let currentPhase = 1;

    // Audio SFX
    const audio = new AudioManager();

    // Canvas size
    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const eventRing = {
        x: width / 2,
        y: height / 2,
        radius: Math.min(width, height) * 0.26,
    };
    const resize = () => {
        width = window.innerWidth;
        height = window.innerHeight;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        eventRing.x = width / 2;
        eventRing.y = height / 2;
        eventRing.radius = Math.min(width, height) * 0.26;
    };
    resize();
    window.addEventListener("resize", resize);

    // Estado del Jugador
    let playerX = width / 2;
    let playerY = height / 2;
    let targetX = width / 2;
    let targetY = height / 2;
    let playerRadius = 34;
    let targetMass = 60;
    let playerElement: ElementType = "fire";
    let speedMultiplier = 1.0;
    let isChannelingPotion = false;
    let potionChannelStart = 0;
    const potionChannelDuration = 500; // 0.5s de canalización
    let isSpeedBoostActive = false;
    let hasShield = false;

    // Partículas visuales, textos flotantes y ondas expansivas
    const particles: Particle[] = [];
    const floatingTexts: FloatingText[] = [];
    const shockwaves: Shockwave[] = [];

    const spawnParticles = (x: number, y: number, color: string, count: number = 16, speedMult: number = 1) => {
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = (Math.random() * 4 + 1.5) * speedMult;
            const maxLife = Math.random() * 26 + 18;
            particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                radius: Math.random() * 4 + 2,
                color,
                alpha: 1,
                life: maxLife,
                maxLife,
            });
        }
    };

    const addFloatingText = (x: number, y: number, text: string, color: string = "#ffffff") => {
        floatingTexts.push({
            x,
            y,
            text,
            color,
            alpha: 1,
            life: 50,
            maxLife: 50,
        });
    };

    // =========================================================================
    // FASE 1: 4 Orbes de energía elemental iniciales
    // =========================================================================
    const orbs: EnergyOrb[] = [];
    const orbColors = ["#fbbf24", "#38bdf8", "#ef4444", "#34d399"];
    const orbRadiusDist = Math.min(width, height) * 0.28;

    for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
        const ox = width / 2 + Math.cos(angle) * orbRadiusDist;
        const oy = height / 2 + Math.sin(angle) * orbRadiusDist;
        orbs.push({
            x: ox,
            y: oy,
            baseX: ox,
            baseY: oy,
            radius: 14,
            color: orbColors[i],
            glowColor: orbColors[i],
            pulseOffset: i * 1.5,
            collected: false,
        });
    }
    let orbsCollectedCount = 0;

    // =========================================================================
    // FASE 2 y 3: Gólem de Fuego 🔥
    // =========================================================================
    let golemMass = 170;
    let golemRadius = Math.sqrt(golemMass) * 4.2;
    let golemX = width / 2 + 180;
    let golemY = height / 2 - 80;
    let golemTargetX = golemX;
    let golemTargetY = golemY;
    let golemAlive = true;
    let lastGolemDirChange = 0;
    let lastDrainTime = 0;
    let lastPushWarningTime = 0;

    // =========================================================================
    // FASE 5: Reliquia de Power-Up Escudo 🛡️ y Titán Voltaico ⚡
    // =========================================================================
    const shieldRelic = {
        x: width / 2 + 160,
        y: height / 2 - 60,
        radius: 22,
        active: false,
    };
    let titanX = width / 2 - 200;
    let titanY = height / 2 + 80;
    const titanRadius = 56;
    let titanAlive = false;
    let titanVx = 0;
    let titanVy = 0;
    let titanKnockedBack = false;

    // =========================================================================
    // FASE 6: Evento Lluvia de Comida 🍬
    // =========================================================================
    const eventFoods: EventFood[] = [];
    let eventFoodsCollected = 0;

    const initPhase6 = () => {
        eventRing.x = width / 2;
        eventRing.y = height / 2;
        eventRing.radius = Math.min(width, height) * 0.26;
        eventFoods.length = 0;
        eventFoodsCollected = 0;

        for (let i = 0; i < 6; i++) {
            const angle = (i / 6) * Math.PI * 2;
            const r = eventRing.radius * (0.35 + (i % 2) * 0.38);
            eventFoods.push({
                x: eventRing.x + Math.cos(angle) * r,
                y: eventRing.y + Math.sin(angle) * r,
                radius: 13,
                collected: false,
                pulseOffset: i * 1.1,
            });
        }
        updatePhaseUI();
        audio.playSuccessSfx();
    };

    // Tracking de input del puntero (ratón o táctil)
    const onPointerMove = (e: MouseEvent | TouchEvent) => {
        if (!isActive || isPaused) return;
        if ("touches" in e) {
            if (e.touches.length > 0) {
                targetX = e.touches[0].clientX;
                targetY = e.touches[0].clientY;
            }
        } else {
            targetX = (e as MouseEvent).clientX;
            targetY = (e as MouseEvent).clientY;
        }
    };

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("touchmove", onPointerMove, { passive: true });

    // Cambio de elemento
    const switchElement = (el: ElementType) => {
        if (currentPhase < 2) return;
        playerElement = el;
        elBtns.forEach((b) => {
            const matches = b.dataset.el === el;
            b.classList.toggle("active", matches);
            if (matches && el === "water") {
                b.classList.remove("highlight-pulse");
            }
        });
        audio.playBoostSfx();
        if (currentPhase === 2) {
            updatePhaseUI();
        }
    };

    // Activación de poción de velocidad
    const triggerPotion = () => {
        if (currentPhase === 4 && !isChannelingPotion && !isSpeedBoostActive) {
            isChannelingPotion = true;
            potionChannelStart = performance.now();
        }
    };

    // Resetear estado visual de los botones elementales (Fuego activo, Agua con pulso guía)
    elBtns.forEach((b) => {
        const el = b.dataset.el;
        b.classList.toggle("active", el === "fire");
        b.classList.toggle("highlight-pulse", el === "water");
    });

    // Listeners de botones de elementos y poción (asignación directa para evitar acumulación)
    elBtns.forEach((btn) => {
        btn.onclick = () => {
            const el = btn.dataset.el as ElementType | undefined;
            if (el && el in ELEMENT_DATA) {
                switchElement(el);
            }
        };
    });

    if (tutorialPotionBtn) {
        tutorialPotionBtn.onclick = () => {
            triggerPotion();
        };
    }

    // Listener de teclado [1-5], [Q] y [Escape]
    const onKeyDown = (e: KeyboardEvent) => {
        if (!isActive) return;
        if (e.key === "Escape") {
            cleanup();
            return;
        }
        if (isPaused) return;

        if (e.key === "1") switchElement("fire");
        else if (e.key === "2") switchElement("water");
        else if (e.key === "3") switchElement("plant");
        else if (e.key === "4") switchElement("electric");
        else if (e.key === "5") switchElement("earth");
        else if (e.key.toLowerCase() === "q") {
            triggerPotion();
        }
    };
    window.addEventListener("keydown", onKeyDown);

    // Actualización de textos de fase (1 a 6)
    const updatePhaseUI = () => {
        if (currentPhase === 1) {
            if (phaseTag) phaseTag.textContent = "FASE 1 / 6";
            if (phaseTitle) phaseTitle.textContent = "Movimiento y Crecimiento";
            if (phaseDesc) phaseDesc.textContent = "Mueve el ratón o el dedo para absorber las 4 esencias elementales y crecer.";
            if (progressBar) progressBar.style.width = `${(orbsCollectedCount / 4) * 100}%`;
            if (tutorialControlsWrap) tutorialControlsWrap.classList.add("hidden");
        } else if (currentPhase === 2) {
            if (phaseTag) phaseTag.textContent = "FASE 2 / 6";
            if (phaseTitle) phaseTitle.textContent = "Cambio de Elemento [1-5] y Robo por Contacto";
            if (phaseDesc) {
                if (playerElement !== "water") {
                    phaseDesc.textContent = "¡Un Gólem de Fuego 🔥 gigante! Pulsa la tecla [2] o toca el botón 💧 Agua abajo para activar tu ventaja elemental.";
                } else {
                    phaseDesc.textContent = "¡Eres Agua 💧! Toca con tu borde la FRANJA VERDE EXTERIOR del Gólem para robarle masa, ¡pero NO metas tu centro en su CÍRCULO ROJO MORTAL!";
                }
            }
            if (progressBar) progressBar.style.width = "35%";
            if (tutorialControlsWrap) tutorialControlsWrap.classList.remove("hidden");
            if (tutorialElementBar) tutorialElementBar.classList.remove("hidden");
            if (tutorialPotionBtn) tutorialPotionBtn.classList.add("hidden");
        } else if (currentPhase === 3) {
            if (phaseTag) phaseTag.textContent = "FASE 3 / 6";
            if (phaseTitle) phaseTitle.textContent = "Devoración por Masa Superior";
            if (phaseDesc) phaseDesc.textContent = "¡Le robaste suficiente masa! El anillo rojo desapareció (ahora eres +25% más grande). ¡Lánzate al centro del Gólem para devorarlo!";
            if (progressBar) progressBar.style.width = "50%";
            if (tutorialControlsWrap) tutorialControlsWrap.classList.remove("hidden");
            if (tutorialElementBar) tutorialElementBar.classList.remove("hidden");
            if (tutorialPotionBtn) tutorialPotionBtn.classList.add("hidden");
        } else if (currentPhase === 4) {
            if (phaseTag) phaseTag.textContent = "FASE 4 / 6";
            if (phaseTitle) phaseTitle.textContent = "Pociones Tácticas [Q/W/E]";
            if (phaseDesc) phaseDesc.textContent = "En la tienda puedes comprar pociones tácticas. Pulsa [Q] o toca el botón de poción abajo para beberla (0.5s de canalización).";
            if (progressBar) progressBar.style.width = "65%";
            if (tutorialControlsWrap) tutorialControlsWrap.classList.remove("hidden");
            if (tutorialElementBar) tutorialElementBar.classList.add("hidden");
            if (tutorialPotionBtn) tutorialPotionBtn.classList.remove("hidden");
        } else if (currentPhase === 5) {
            if (phaseTag) phaseTag.textContent = "FASE 5 / 6";
            if (phaseTitle) phaseTitle.textContent = "Reliquias y Power-Ups: Escudo 🛡️";
            if (phaseDesc) {
                if (!hasShield) {
                    phaseDesc.textContent = "Paso 1: Recoge la reliquia de Escudo 🛡️ azul en el mapa para protegerte.";
                } else {
                    phaseDesc.textContent = "Paso 2: ¡Escudo activo! Ahora TOCA al Titán Voltaico ⚡ para ver cómo el Escudo te salva la vida y lo empuja lejos.";
                }
            }
            if (progressBar) progressBar.style.width = "80%";
            if (tutorialControlsWrap) tutorialControlsWrap.classList.add("hidden");
        } else if (currentPhase === 6) {
            if (phaseTag) phaseTag.textContent = "FASE 6 / 6";
            if (phaseTitle) phaseTitle.textContent = "Eventos Dinámicos de la Arena";
            if (phaseDesc) {
                phaseDesc.textContent = `¡Se ha activado el evento Lluvia de Comida 🍬! Entra al círculo dorado y come las 6 esencias de bonificación (${eventFoodsCollected}/6).`;
            }
            if (progressBar) progressBar.style.width = `${90 + (eventFoodsCollected / 6) * 10}%`;
            if (tutorialControlsWrap) tutorialControlsWrap.classList.add("hidden");
        }
    };
    updatePhaseUI();

    // Limpieza y cierre
    const cleanup = () => {
        isActive = false;
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        window.removeEventListener("resize", resize);
        window.removeEventListener("mousemove", onPointerMove);
        window.removeEventListener("touchmove", onPointerMove);
        window.removeEventListener("keydown", onKeyDown);

        overlay.classList.add("hidden");
        elBtns.forEach((b) => { b.onclick = null; });
        if (tutorialPotionBtn) tutorialPotionBtn.onclick = null;
        if (tutorialControlsWrap) tutorialControlsWrap.classList.add("hidden");
        if (tutorialPowerupsModal) tutorialPowerupsModal.classList.add("hidden");
        if (tutorialEventsModal) tutorialEventsModal.classList.add("hidden");
        if (wheelModal) wheelModal.classList.add("hidden");
        if (finishModal) finishModal.classList.add("hidden");

        if (aliasModal) {
            aliasModal.classList.remove("hidden");
            document.body.classList.add("in-menu");
        }
        window.dispatchEvent(new CustomEvent("alchem:refresh_index"));
    };

    if (exitBtn) exitBtn.onclick = cleanup;
    if (toArenaBtn) toArenaBtn.onclick = cleanup;

    // Finalización autoritativa y acreditación
    const handleTutorialCompletion = async () => {
        isPaused = true;
        currentPhase = 6;
        if (progressBar) progressBar.style.width = "100%";
        if (phaseTag) phaseTag.textContent = "FASE 6 / 6";
        if (phaseTitle) phaseTitle.textContent = "Entrenamiento Completado";
        if (phaseDesc) phaseDesc.textContent = "¡Has dominado los principios, tácticas y eventos de combate de Alchem.io!";

        if (finishModal) finishModal.classList.remove("hidden");

        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        const isRegistered = !options.isGuest && Boolean(token);

        if (isRegistered && !options.alreadyClaimed) {
            try {
                const res = await fetch("/api/tutorial/claim-reward", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await res.json();
                if (res.ok && data.success) {
                    safeSetStorage("alchem_tutorial_reward_claimed", "true");
                    await questManager.fetchServerState();
                    window.dispatchEvent(new CustomEvent("alchem:auth_change", { detail: { crystals: data.crystals } }));

                    if (finishTitle) finishTitle.textContent = "¡Entrenamiento Superado!";
                    if (finishDesc) finishDesc.textContent = "¡Felicidades! +50 Cristales Alquímicos 💎 acreditados autoritativamente a tu cuenta.";
                    if (finishBadge) {
                        finishBadge.textContent = "+50 💎 ACREDITADOS";
                        finishBadge.classList.remove("hidden");
                    }
                    audio.playSuccessSfx();
                    return;
                }
            } catch (err) {
                console.warn("[TutorialArena] Error al acreditar recompensa:", err);
            }
        }

        // Modo invitado o ya reclamado
        if (finishTitle) finishTitle.textContent = "¡Excelente práctica!";
        if (finishDesc) finishDesc.textContent = "Has dominado los fundamentos de combate. ¡Entra a la arena y conquista la cima!";
        if (finishBadge) finishBadge.classList.add("hidden");
        audio.playSuccessSfx();
    };

    // Botón de continuar modal de Power-Ups (Fase 5)
    if (tutorialPowerupsContinueBtn) {
        tutorialPowerupsContinueBtn.onclick = () => {
            if (tutorialPowerupsModal) tutorialPowerupsModal.classList.add("hidden");
            isPaused = false;

            // Iniciar ejercicio práctico de Escudo y Titán Voltaico
            shieldRelic.x = Math.max(90, Math.min(width - 90, playerX + (playerX > width / 2 ? -200 : 200)));
            shieldRelic.y = Math.max(90, Math.min(height - 90, playerY - 80));
            shieldRelic.active = true;

            titanX = width / 2 + (playerX > width / 2 ? -260 : 260);
            titanY = height / 2 + 60;
            titanAlive = true;
            titanKnockedBack = false;
            titanVx = 0;
            titanVy = 0;

            updatePhaseUI();
        };
    }

    // Botón de continuar modal de eventos (Fase 6)
    if (tutorialEventsContinueBtn) {
        tutorialEventsContinueBtn.onclick = () => {
            if (tutorialEventsModal) tutorialEventsModal.classList.add("hidden");
            void handleTutorialCompletion();
        };
    }

    // Bucle principal a 60 FPS
    let lastTime = performance.now();

    const loop = (timestamp: number) => {
        if (!isActive) return;
        animationFrameId = requestAnimationFrame(loop);

        const dt = Math.min((timestamp - lastTime) / 1000, 0.1);
        lastTime = timestamp;

        // Si hay un modal abierto, pausar física y colisiones
        if (!isPaused) {
            // 1. Mover jugador hacia target (lerp suave)
            const dx = targetX - playerX;
            const dy = targetY - playerY;
            const dist = Math.hypot(dx, dy);
            const speed = Math.min(dist * 6, 420) * speedMultiplier;
            if (dist > 2) {
                playerX += (dx / dist) * speed * dt;
                playerY += (dy / dist) * speed * dt;
            }

            // Restricciones de pantalla
            playerX = Math.max(playerRadius, Math.min(width - playerRadius, playerX));
            playerY = Math.max(playerRadius, Math.min(height - playerRadius, playerY));

            // Expansión suave de masa
            playerRadius += (Math.sqrt(targetMass) * 4.2 - playerRadius) * 0.1;

            // Actualizar Gólem si está vivo (movimiento suave y lento para facilitar contacto)
            if ((currentPhase === 2 || currentPhase === 3) && golemAlive) {
                if (timestamp - lastGolemDirChange > 1600) {
                    lastGolemDirChange = timestamp;
                    golemTargetX = width / 2 + (Math.random() - 0.5) * (width * 0.35);
                    golemTargetY = height / 2 + (Math.random() - 0.5) * (height * 0.35);
                }
                const gdx = golemTargetX - golemX;
                const gdy = golemTargetY - golemY;
                const gdist = Math.hypot(gdx, gdy);
                if (gdist > 5) {
                    const gSpeed = currentPhase === 2 ? 22 : 85;
                    golemX += (gdx / gdist) * gSpeed * dt;
                    golemY += (gdy / gdist) * gSpeed * dt;
                }
                golemX = Math.max(golemRadius + 10, Math.min(width - golemRadius - 10, golemX));
                golemY = Math.max(golemRadius + 10, Math.min(height - golemRadius - 10, golemY));
                golemRadius += (Math.sqrt(golemMass) * 4.2 - golemRadius) * 0.1;
            }

            // =====================================================================
            // LÓGICA DE FASES
            // =====================================================================

            // Fase 1: Absorción de 4 orbes
            if (currentPhase === 1) {
                for (const orb of orbs) {
                    if (orb.collected) continue;

                    const floatOffset = Math.sin(timestamp * 0.003 + orb.pulseOffset) * 6;
                    orb.y = orb.baseY + floatOffset;

                    const dOrb = Math.hypot(playerX - orb.x, playerY - orb.y);
                    if (dOrb < playerRadius + orb.radius) {
                        orb.collected = true;
                        orbsCollectedCount++;
                        targetMass += 12; // Masa ~108 tras 4 orbes
                        audio.playEatSfx();
                        spawnParticles(orb.x, orb.y, orb.color, 20);
                        updatePhaseUI();

                        if (orbsCollectedCount >= 4) {
                            setTimeout(() => {
                                if (isActive && currentPhase === 1) {
                                    currentPhase = 2;
                                    updatePhaseUI();
                                    audio.playSuccessSfx();
                                }
                            }, 400);
                        }
                    }
                }
            }

            // Fase 2: Robo de masa por contacto físico en franja exterior
            if (currentPhase === 2 && golemAlive) {
                const combatDist = Math.hypot(playerX - golemX, playerY - golemY);

                if (playerElement === "water") {
                    const innerDangerRadius = golemRadius * 0.65;
                    const isTouchingOuterStrip = combatDist <= playerRadius + golemRadius + 2;
                    const centerOutsideMortalZone = combatDist > innerDangerRadius + playerRadius * 0.25;

                    // Contacto físico en franja exterior segura
                    if (isTouchingOuterStrip && centerOutsideMortalZone) {
                        if (timestamp - lastDrainTime > 200) {
                            lastDrainTime = timestamp;
                            golemMass = Math.max(40, golemMass - 14);
                            targetMass += 12;

                            // Punto exacto de contacto entre las dos circunferencias
                            const angle = Math.atan2(golemY - playerY, golemX - playerX);
                            const contactX = playerX + Math.cos(angle) * playerRadius;
                            const contactY = playerY + Math.sin(angle) * playerRadius;

                            spawnParticles(contactX, contactY, "#38bdf8", 16, 1.5);
                            addFloatingText(contactX, contactY - 12, "+12 MASA 💧 (CONTACTO)", "#38bdf8");
                            audio.playBoostSfx();

                            if (targetMass >= golemMass * 1.25) {
                                setTimeout(() => {
                                    if (isActive && currentPhase === 2) {
                                        currentPhase = 3;
                                        updatePhaseUI();
                                        audio.playSuccessSfx();
                                    }
                                }, 350);
                            }
                        }
                    }

                    // Si el jugador mete su centro al círculo rojo mortal mientras el Gólem es peligroso, empujar afuera
                    if (combatDist <= innerDangerRadius + playerRadius * 0.25) {
                        const pushAngle = Math.atan2(playerY - golemY, playerX - golemX);
                        playerX = golemX + Math.cos(pushAngle) * (innerDangerRadius + playerRadius * 0.35 + 16);
                        playerY = golemY + Math.sin(pushAngle) * (innerDangerRadius + playerRadius * 0.35 + 16);
                        targetX = playerX;
                        targetY = playerY;
                        if (timestamp - lastPushWarningTime > 500) {
                            lastPushWarningTime = timestamp;
                            addFloatingText(playerX, playerY - playerRadius - 15, "💀 ¡Muy adentro! Ahí te devoraría", "#ef4444");
                        }
                    }
                } else {
                    // Si aún no es Agua y toca al Gólem, rebotar suavemente y recordar cambiar
                    if (combatDist <= playerRadius + golemRadius) {
                        const pushAngle = Math.atan2(playerY - golemY, playerX - golemX);
                        playerX = golemX + Math.cos(pushAngle) * (playerRadius + golemRadius + 8);
                        playerY = golemY + Math.sin(pushAngle) * (playerRadius + golemRadius + 8);
                        targetX = playerX;
                        targetY = playerY;
                        if (timestamp - lastPushWarningTime > 600) {
                            lastPushWarningTime = timestamp;
                            addFloatingText(playerX, playerY - playerRadius - 15, "💧 ¡Cambia a Agua [2] para vencer al Fuego!", "#38bdf8");
                        }
                    }
                }
            }

            // Fase 3: Devoración al impactar centro del Gólem
            if (currentPhase === 3 && golemAlive) {
                const combatDist = Math.hypot(playerX - golemX, playerY - golemY);
                if (combatDist < playerRadius) {
                    golemAlive = false;
                    targetMass += 50;
                    spawnParticles(golemX, golemY, "#ef4444", 45, 1.8);
                    spawnParticles(golemX, golemY, "#22c55e", 30, 1.5);
                    addFloatingText(playerX, playerY - playerRadius - 15, "¡DEVORADO! +50 💥", "#22c55e");
                    audio.playCounterSfx();

                    setTimeout(() => {
                        if (isActive && currentPhase === 3) {
                            currentPhase = 4;
                            updatePhaseUI();
                            audio.playSuccessSfx();
                        }
                    }, 600);
                }
            }

            // Fase 4: Pociones tácticas (canalización y velocidad)
            if (currentPhase === 4 && isChannelingPotion) {
                const elapsed = timestamp - potionChannelStart;
                if (elapsed >= potionChannelDuration) {
                    isChannelingPotion = false;
                    isSpeedBoostActive = true;
                    speedMultiplier = 1.6;
                    audio.playBoostSfx();
                    spawnParticles(playerX, playerY, "#38bdf8", 30, 2);
                    spawnParticles(playerX, playerY, "#fbbf24", 20, 1.5);
                    addFloatingText(playerX, playerY - playerRadius - 15, "⚡ VELOCIDAD x1.6", "#38bdf8");
                    if (tutorialPotionBtn) tutorialPotionBtn.classList.add("hidden");

                    setTimeout(() => {
                        if (isActive && currentPhase === 4) {
                            currentPhase = 5;
                            updatePhaseUI();
                            // Mostrar primero el modal de los 5 Power-Ups
                            if (tutorialPowerupsModal) {
                                tutorialPowerupsModal.classList.remove("hidden");
                                isPaused = true;
                            }
                        }
                    }, 500);
                }
            }

            // Fase 5: Power-Ups (Escudo contra Titán Voltaico)
            if (currentPhase === 5) {
                // 5A: Recoger reliquia de escudo
                if (shieldRelic.active && !hasShield) {
                    const dShield = Math.hypot(playerX - shieldRelic.x, playerY - shieldRelic.y);
                    if (dShield < playerRadius + shieldRelic.radius) {
                        shieldRelic.active = false;
                        hasShield = true;
                        audio.playSuccessSfx();
                        spawnParticles(shieldRelic.x, shieldRelic.y, "#38bdf8", 35, 1.8);
                        addFloatingText(playerX, playerY - playerRadius - 15, "🛡️ ESCUDO ACTIVO", "#38bdf8");
                        updatePhaseUI();
                    }
                }

                // 5B: Titán Voltaico
                if (titanAlive) {
                    if (!titanKnockedBack) {
                        // Titán se acerca lentamente solo si el jugador tiene el escudo
                        if (hasShield) {
                            const tdx = playerX - titanX;
                            const tdy = playerY - titanY;
                            const tdist = Math.hypot(tdx, tdy);
                            if (tdist > 5) {
                                titanX += (tdx / tdist) * 65 * dt;
                                titanY += (tdy / tdist) * 65 * dt;
                            }
                        }

                        // Colisión con jugador
                        const dTitan = Math.hypot(playerX - titanX, playerY - titanY);
                        if (dTitan <= playerRadius + titanRadius) {
                            if (hasShield) {
                                hasShield = false;
                                shockwaves.push({
                                    x: (playerX + titanX) / 2,
                                    y: (playerY + titanY) / 2,
                                    radius: 12,
                                    maxRadius: 130,
                                    alpha: 1,
                                });
                                spawnParticles(titanX, titanY, "#38bdf8", 35, 2.5);
                                spawnParticles(titanX, titanY, "#ffffff", 15, 1.8);
                                audio.playCounterSfx();
                                addFloatingText(playerX, playerY - playerRadius - 20, "🛡️ ¡ESCUDO BLOQUEÓ EL ATAQUE!", "#38bdf8");

                                titanKnockedBack = true;
                                const kAngle = Math.atan2(titanY - playerY, titanX - playerX);
                                titanVx = Math.cos(kAngle) * 920;
                                titanVy = Math.sin(kAngle) * 920;

                                setTimeout(() => {
                                    if (isActive && currentPhase === 5) {
                                        currentPhase = 6;
                                        titanAlive = false;
                                        initPhase6();
                                    }
                                }, 900);
                            } else {
                                const pAngle = Math.atan2(playerY - titanY, playerX - titanX);
                                playerX = titanX + Math.cos(pAngle) * (playerRadius + titanRadius + 8);
                                playerY = titanY + Math.sin(pAngle) * (playerRadius + titanRadius + 8);
                                targetX = playerX;
                                targetY = playerY;
                                if (timestamp - lastPushWarningTime > 500) {
                                    lastPushWarningTime = timestamp;
                                    addFloatingText(playerX, playerY - playerRadius - 15, "🛡️ ¡Primero recoge el Escudo!", "#38bdf8");
                                }
                            }
                        }
                    } else {
                        // Movimiento por knockback rápido hacia afuera
                        titanX += titanVx * dt;
                        titanY += titanVy * dt;
                        titanVx *= 0.94;
                        titanVy *= 0.94;
                    }
                }
            }

            // Fase 6: Evento Lluvia de Comida
            if (currentPhase === 6) {
                for (const food of eventFoods) {
                    if (food.collected) continue;

                    const dFood = Math.hypot(playerX - food.x, playerY - food.y);
                    if (dFood < playerRadius + food.radius) {
                        food.collected = true;
                        eventFoodsCollected++;
                        targetMass += 20;
                        audio.playEatSfx();
                        spawnParticles(food.x, food.y, "#fbbf24", 22, 1.6);
                        addFloatingText(food.x, food.y - 10, "+20 MASA 🍬", "#fbbf24");
                        updatePhaseUI();

                        if (eventFoodsCollected >= 6) {
                            setTimeout(() => {
                                if (isActive && currentPhase === 6) {
                                    isPaused = true;
                                    if (tutorialEventsModal) {
                                        tutorialEventsModal.classList.remove("hidden");
                                    }
                                }
                            }, 400);
                        }
                    }
                }
            }

            // Actualizar partículas
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;
                p.life--;
                p.alpha = Math.max(0, p.life / p.maxLife);
                if (p.life <= 0) {
                    particles.splice(i, 1);
                }
            }

            // Actualizar textos flotantes
            for (let i = floatingTexts.length - 1; i >= 0; i--) {
                const ft = floatingTexts[i];
                ft.y -= 0.8;
                ft.life--;
                ft.alpha = Math.max(0, ft.life / ft.maxLife);
                if (ft.life <= 0) {
                    floatingTexts.splice(i, 1);
                }
            }

            // Actualizar ondas expansivas
            for (let i = shockwaves.length - 1; i >= 0; i--) {
                const sw = shockwaves[i];
                sw.radius += (sw.maxRadius - sw.radius) * 0.18 + 5;
                sw.alpha = Math.max(0, 1 - sw.radius / sw.maxRadius);
                if (sw.alpha <= 0 || sw.radius >= sw.maxRadius) {
                    shockwaves.splice(i, 1);
                }
            }
        }

        // =====================================================================
        // RENDERIZADO VISUAL
        // =====================================================================
        ctx.clearRect(0, 0, width, height);

        // Fondo cósmico con cuadrícula sutil
        ctx.fillStyle = "#020617";
        ctx.fillRect(0, 0, width, height);

        ctx.strokeStyle = "rgba(56, 189, 248, 0.04)";
        ctx.lineWidth = 1;
        const gridSize = 60;
        for (let x = 0; x < width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        }
        for (let y = 0; y < height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
        }

        // Renderizar ondas expansivas de escudo
        for (const sw of shockwaves) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(56, 189, 248, ${sw.alpha * 0.95})`;
            ctx.lineWidth = 5;
            ctx.stroke();
            ctx.restore();
        }

        // Renderizar partículas
        for (const p of particles) {
            ctx.save();
            ctx.globalAlpha = p.alpha;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        // Renderizar Fase 6: Anillo y Esencias del Evento Lluvia de Comida
        if (currentPhase === 6) {
            ctx.save();
            // Área de evento en el suelo
            const pulse = Math.sin(timestamp * 0.004) * 4;
            const rEvent = eventRing.radius + pulse;

            const rGlow = ctx.createRadialGradient(eventRing.x, eventRing.y, 10, eventRing.x, eventRing.y, rEvent);
            rGlow.addColorStop(0, "rgba(245, 158, 11, 0.14)");
            rGlow.addColorStop(1, "rgba(245, 158, 11, 0.02)");
            ctx.fillStyle = rGlow;
            ctx.beginPath();
            ctx.arc(eventRing.x, eventRing.y, rEvent, 0, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = "rgba(245, 158, 11, 0.8)";
            ctx.lineWidth = 3;
            ctx.setLineDash([10, 8]);
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.font = "bold 13px 'Cinzel', serif";
            ctx.fillStyle = "#fbbf24";
            ctx.textAlign = "center";
            ctx.fillText("🍬 EVENTO ACTIVO: LLUVIA DE COMIDA", eventRing.x, eventRing.y - rEvent + 24);

            // Esencias / dulces de comida
            for (const food of eventFoods) {
                if (food.collected) continue;
                const fPulse = Math.sin(timestamp * 0.005 + food.pulseOffset) * 2;
                const fr = food.radius + fPulse;

                ctx.beginPath();
                ctx.arc(food.x, food.y, fr * 1.8, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(245, 158, 11, 0.3)";
                ctx.fill();

                ctx.beginPath();
                ctx.arc(food.x, food.y, fr, 0, Math.PI * 2);
                ctx.fillStyle = "#f59e0b";
                ctx.fill();
                ctx.strokeStyle = "#fef08a";
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.font = "14px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("🍬", food.x, food.y);
            }
            ctx.restore();
        }

        // Renderizar orbes de energía (Fase 1)
        if (currentPhase === 1) {
            for (const orb of orbs) {
                if (orb.collected) continue;

                const glow = ctx.createRadialGradient(orb.x, orb.y, orb.radius * 0.2, orb.x, orb.y, orb.radius * 2.2);
                glow.addColorStop(0, orb.glowColor);
                glow.addColorStop(1, "rgba(0, 0, 0, 0)");
                ctx.fillStyle = glow;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.radius * 2.2, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = orb.color;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
                ctx.fill();

                ctx.strokeStyle = "#ffffff";
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(orb.x - 3, orb.y - 3, 3.5, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Renderizar Gólem de Fuego (Fase 2 y 3)
        if ((currentPhase === 2 || currentPhase === 3) && golemAlive) {
            // Halo de Fuego
            const gGlow = ctx.createRadialGradient(golemX, golemY, golemRadius * 0.2, golemX, golemY, golemRadius * 1.8);
            gGlow.addColorStop(0, "rgba(239, 68, 68, 0.45)");
            gGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
            ctx.fillStyle = gGlow;
            ctx.beginPath();
            ctx.arc(golemX, golemY, golemRadius * 1.8, 0, Math.PI * 2);
            ctx.fill();

            // Cuerpo del bot
            ctx.fillStyle = "#991b1b";
            ctx.beginPath();
            ctx.arc(golemX, golemY, golemRadius, 0, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = "#f87171";
            ctx.lineWidth = 3;
            ctx.stroke();

            // Icono / Rótulo del Gólem
            ctx.fillStyle = "#ffffff";
            ctx.font = `bold ${Math.round(Math.max(16, golemRadius * 0.7))}px sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText("🔥", golemX, golemY - 2);

            ctx.font = "bold 12px Cinzel, sans-serif";
            ctx.fillStyle = "#fca5a5";
            const gTitle = currentPhase === 2 ? "Gólem de Fuego 🔥" : "Gólem Debilitado 🔥";
            ctx.fillText(gTitle, golemX, golemY + golemRadius + 18);

            // Fase 2: Anillos de Contacto Seguro (Verde) y Zona de Muerte (Rojo)
            if (currentPhase === 2) {
                ctx.save();
                // 1. Franja verde exterior de contacto seguro
                ctx.beginPath();
                ctx.arc(golemX, golemY, golemRadius, 0, Math.PI * 2);
                ctx.strokeStyle = "#22c55e";
                ctx.lineWidth = 2.5;
                ctx.setLineDash([6, 5]);
                ctx.stroke();

                // 2. Círculo rojo interior mortal
                const innerDangerRadius = golemRadius * 0.65;
                ctx.beginPath();
                ctx.arc(golemX, golemY, innerDangerRadius, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(239, 68, 68, 0.22)";
                ctx.fill();

                ctx.strokeStyle = "rgba(239, 68, 68, 0.95)";
                ctx.lineWidth = 3;
                ctx.setLineDash([8, 6]);
                ctx.stroke();
                ctx.setLineDash([]);

                ctx.font = "bold 10px 'Outfit', sans-serif";
                ctx.fillStyle = "#fca5a5";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("💀 ZONA DE MUERTE", golemX, golemY);
                ctx.restore();
            }

            // Anillo Verde Brillante de Devorar (Fase 3 cuando Gólem es pequeño)
            if (currentPhase === 3) {
                ctx.save();
                const devourRadius = golemRadius + 12;
                ctx.beginPath();
                ctx.arc(golemX, golemY, devourRadius, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(34, 197, 94, 0.15)";
                ctx.fill();

                ctx.strokeStyle = "#22c55e";
                ctx.lineWidth = 3;
                ctx.setLineDash([6, 5]);
                ctx.stroke();
                ctx.setLineDash([]);

                ctx.font = "bold 11px 'Outfit', sans-serif";
                ctx.fillStyle = "#86efac";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("✓ DEVORAR", golemX, golemY - devourRadius - 10);
                ctx.restore();
            }
        }

        // Renderizar Power-Up Reliquia de Escudo 🛡️ (Fase 5)
        if (currentPhase === 5 && shieldRelic.active) {
            ctx.save();
            const pulse = Math.sin(timestamp * 0.005) * 3;
            const rRelic = shieldRelic.radius + pulse;

            const rGlow = ctx.createRadialGradient(shieldRelic.x, shieldRelic.y, 4, shieldRelic.x, shieldRelic.y, rRelic * 2);
            rGlow.addColorStop(0, "rgba(56, 189, 248, 0.55)");
            rGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
            ctx.fillStyle = rGlow;
            ctx.beginPath();
            ctx.arc(shieldRelic.x, shieldRelic.y, rRelic * 2, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
            ctx.beginPath();
            ctx.arc(shieldRelic.x, shieldRelic.y, rRelic, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#38bdf8";
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.font = "20px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText("🛡️", shieldRelic.x, shieldRelic.y);

            ctx.font = "bold 11px 'Outfit', sans-serif";
            ctx.fillStyle = "#7dd3fc";
            ctx.fillText("Escudo Alquímico", shieldRelic.x, shieldRelic.y + rRelic + 14);
            ctx.restore();
        }

        // Renderizar Titán Voltaico ⚡ (Fase 5)
        if (currentPhase === 5 && titanAlive) {
            ctx.save();
            const tGlow = ctx.createRadialGradient(titanX, titanY, titanRadius * 0.2, titanX, titanY, titanRadius * 1.8);
            tGlow.addColorStop(0, "rgba(234, 179, 8, 0.45)");
            tGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
            ctx.fillStyle = tGlow;
            ctx.beginPath();
            ctx.arc(titanX, titanY, titanRadius * 1.8, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = "#581c87";
            ctx.beginPath();
            ctx.arc(titanX, titanY, titanRadius, 0, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = "#eab308";
            ctx.lineWidth = 4;
            ctx.stroke();

            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 26px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText("⚡", titanX, titanY - 2);

            ctx.font = "bold 13px Cinzel, sans-serif";
            ctx.fillStyle = "#fde047";
            ctx.fillText("Titán Voltaico ⚡", titanX, titanY + titanRadius + 18);
            ctx.restore();
        }

        // Renderizar Jugador
        const elData = ELEMENT_DATA[playerElement] || ELEMENT_DATA.fire;
        const pGlow = ctx.createRadialGradient(playerX, playerY, playerRadius * 0.3, playerX, playerY, playerRadius * 2);
        pGlow.addColorStop(0, elData.glow);
        pGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = pGlow;
        ctx.beginPath();
        ctx.arc(playerX, playerY, playerRadius * 2, 0, Math.PI * 2);
        ctx.fill();

        // Efecto aura de velocidad activa
        if (isSpeedBoostActive) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(playerX, playerY, playerRadius + 6, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(56, 189, 248, 0.6)";
            ctx.lineWidth = 2.5;
            ctx.stroke();
            ctx.restore();
            if (Math.random() < 0.3) {
                spawnParticles(playerX, playerY, "#38bdf8", 1, 0.6);
            }
        }

        // Cuerpo del jugador
        ctx.fillStyle = elData.fill;
        ctx.beginPath();
        ctx.arc(playerX, playerY, playerRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = elData.stroke;
        ctx.lineWidth = 3.5;
        ctx.stroke();

        // Burbuja de Escudo activa
        if (hasShield) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(playerX, playerY, playerRadius + 11, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(56, 189, 248, 0.14)";
            ctx.fill();
            ctx.strokeStyle = "rgba(56, 189, 248, 0.88)";
            ctx.lineWidth = 3.5;
            ctx.setLineDash([5, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.restore();
        }

        // Arco de canalización de poción (Fase 4)
        if (isChannelingPotion) {
            ctx.save();
            const elapsed = performance.now() - potionChannelStart;
            const pct = Math.min(1, elapsed / potionChannelDuration);

            ctx.beginPath();
            ctx.arc(playerX, playerY, playerRadius + 14, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct);
            ctx.strokeStyle = "#fbbf24";
            ctx.lineWidth = 4.5;
            ctx.lineCap = "round";
            ctx.shadowColor = "#fbbf24";
            ctx.shadowBlur = 10;
            ctx.stroke();

            ctx.font = "bold 13px 'Outfit', sans-serif";
            ctx.fillStyle = "#fbbf24";
            ctx.textAlign = "center";
            ctx.shadowColor = "rgba(0,0,0,0.8)";
            ctx.shadowBlur = 4;
            ctx.fillText("🧪 Canalizando...", playerX, playerY - playerRadius - 26);
            ctx.restore();
        }

        // Icono elemental en el centro
        ctx.fillStyle = "#ffffff";
        ctx.font = `bold ${Math.round(playerRadius * 0.75)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(elData.icon, playerX, playerY - 1);

        // Nombre del jugador
        ctx.font = "bold 12px Cinzel, sans-serif";
        ctx.fillStyle = "#fbbf24";
        ctx.fillText(`Tú (${elData.name} ${elData.icon})`, playerX, playerY + playerRadius + 18);

        // Renderizar textos flotantes
        for (const ft of floatingTexts) {
            ctx.save();
            ctx.globalAlpha = ft.alpha;
            ctx.fillStyle = ft.color;
            ctx.font = "bold 14px 'Outfit', sans-serif";
            ctx.textAlign = "center";
            ctx.shadowColor = "rgba(0,0,0,0.85)";
            ctx.shadowBlur = 5;
            ctx.fillText(ft.text, ft.x, ft.y);
            ctx.restore();
        }
    };

    animationFrameId = requestAnimationFrame(loop);
}
